import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { reservasPublicasAPI } from '../services/api';
import './ReservaPublica.css';

const STEP_CALENDAR = 1;
const STEP_SLOTS = 2;
const STEP_FORM = 3;
const STEP_CONFIRM = 4;

const TRANSLATIONS = {
  es: {
    loading: 'Cargando...',
    notAvailable: 'Reservas no disponibles',
    notAvailableDesc: 'El sistema de reservas no está habilitado en este momento. Contacta directamente con el profesional.',
    defaultTitle: 'Reserva de cita',
    pickDay: 'Elige un día',
    weekdays: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'],
    locale: 'es-ES',
    prevMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    changeDay: '← Cambiar día',
    slotsFor: 'Horarios para el',
    loadingSlots: 'Cargando horarios...',
    noSlots: 'No hay horarios disponibles ese día. Elige otro.',
    changeSlot: '← Cambiar horario',
    contactInfo: 'Datos de contacto',
    at: 'a las',
    firstName: 'Nombre',
    lastName: 'Apellidos',
    email: 'Email',
    phone: 'Teléfono',
    modality: 'Modalidad',
    modalities: ['Presencial', 'Online'],
    welcome: 'Bienvenida',
    welcomePlaceholder: '¿Hay algo que quieras comentarnos antes de la cita?',
    submitting: 'Reservando...',
    confirm: 'Confirmar reserva',
    confirmed: 'Reserva confirmada',
    confirmedMsg: 'Tu cita ha quedado reservada.',
    bookAnother: 'Hacer otra reserva',
    formError: 'Completa nombre, apellidos y email',
    bookingError: 'Error al reservar',
  },
  en: {
    loading: 'Loading...',
    notAvailable: 'Bookings not available',
    notAvailableDesc: 'The booking system is not currently enabled. Please contact the professional directly.',
    defaultTitle: 'Book an appointment',
    pickDay: 'Choose a day',
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    locale: 'en-US',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    changeDay: '← Change day',
    slotsFor: 'Available times for',
    loadingSlots: 'Loading times...',
    noSlots: 'No available times for this day. Choose another.',
    changeSlot: '← Change time',
    contactInfo: 'Contact details',
    at: 'at',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    modality: 'Session type',
    modalities: ['In-person', 'Online'],
    welcome: 'Welcome message',
    welcomePlaceholder: 'Is there anything you\'d like to share before the appointment?',
    submitting: 'Booking...',
    confirm: 'Confirm booking',
    confirmed: 'Booking confirmed',
    confirmedMsg: 'Your appointment has been booked.',
    bookAnother: 'Book another',
    formError: 'Please fill in first name, last name and email',
    bookingError: 'Booking failed',
  },
};

function ReservaPublica() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const lang = (searchParams.get('lang') || 'es').toLowerCase();
  const t = useMemo(() => TRANSLATIONS[lang] || TRANSLATIONS.es, [lang]);

  const [step, setStep] = useState(STEP_CALENDAR);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);

  const [month, setMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({
    nombre: '',
    apellidos: '',
    email: '',
    telefono: '',
    modalidad: 'Presencial',
    bienvenida: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reservasPublicasAPI.getCoachInfo(username);
        if (!cancelled) setCoach(data);
      } catch (e) {
        if (!cancelled) {
          if (e.code === 'NOT_FOUND') {
            setNotAvailable(true);
          } else {
            setError(e.message || 'No se pudo cargar');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  useEffect(() => {
    if (!selectedDate || !username) return;
    let cancelled = false;
    setLoadingSlots(true);
    reservasPublicasAPI
      .getSlots(username, selectedDate)
      .then((data) => {
        if (!cancelled) setSlots(data.slots || []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => { cancelled = true; };
  }, [username, selectedDate]);

  const handlePrevMonth = () => {
    setMonth((m) => {
      if (m.month === 0) return { year: m.year - 1, month: 11 };
      return { year: m.year, month: m.month - 1 };
    });
  };

  const handleNextMonth = () => {
    const today = new Date();
    const max = new Date(today.getFullYear() + (coach?.dias_anticipacion_max ?? 30), today.getMonth(), today.getDate());
    setMonth((m) => {
      const next = m.month === 11 ? { year: m.year + 1, month: 0 } : { year: m.year, month: m.month + 1 };
      const nextDate = new Date(next.year, next.month, 1);
      if (nextDate > max) return m;
      return next;
    });
  };

  const getDaysInMonth = () => {
    const first = new Date(month.year, month.month, 1);
    const last = new Date(month.year, month.month + 1, 0);
    // Ajustar para que la semana empiece en Lunes (0=Lun, 6=Dom)
    const startPad = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(d);
    return days;
  };

  const minDate = () => {
    const d = new Date();
    const min = coach?.dias_anticipacion_min ?? 1;
    d.setDate(d.getDate() + min);
    return d.toISOString().slice(0, 10);
  };

  const maxDate = () => {
    const d = new Date();
    const max = coach?.dias_anticipacion_max ?? 30;
    d.setDate(d.getDate() + max);
    return d.toISOString().slice(0, 10);
  };

  const isDatePast = (day) => {
    if (!day) return false;
    const d = new Date(month.year, month.month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const isDateOutOfRange = (day) => {
    if (!day) return false;
    const dateStr = `${month.year}-${String(month.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateStr < minDate() || dateStr > maxDate();
  };

  const isDateUnavailable = (day) => {
    if (!day) return false;
    if (coach?.dias_disponibles && coach.dias_disponibles.length > 0) {
      const dow = new Date(month.year, month.month, day).getDay();
      if (!coach.dias_disponibles.includes(dow)) return true;
    }
    return false;
  };

  const isDateToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return month.year === today.getFullYear() && month.month === today.getMonth() && day === today.getDate();
  };

  const isDateSelectable = (day) => {
    if (!day) return false;
    return !isDatePast(day) && !isDateOutOfRange(day) && !isDateUnavailable(day);
  };

  const selectDate = (day) => {
    if (!isDateSelectable(day)) return;
    const dateStr = `${month.year}-${String(month.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setStep(STEP_SLOTS);
  };

  const selectSlot = (slot) => {
    setSelectedSlot(slot);
    setStep(STEP_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const submitBooking = async () => {
    const { nombre, apellidos, email, telefono, modalidad, bienvenida } = form;
    if (!nombre?.trim() || !apellidos?.trim() || !email?.trim() || !selectedDate || !selectedSlot) {
      setError(t.formError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const fechaHora = `${selectedDate} ${selectedSlot}`;
      const result = await reservasPublicasAPI.createBooking({
        usuario_username: username,
        nombre: nombre.trim(),
        apellidos: apellidos.trim(),
        email: email.trim(),
        telefono: (telefono || '').trim(),
        modalidad: modalidad || 'Presencial',
        bienvenida: (bienvenida || '').trim(),
        fecha_hora: fechaHora,
      });
      setConfirmResult(result);
      setStep(STEP_CONFIRM);
    } catch (e) {
      setError(e.message || t.bookingError);
    } finally {
      setSubmitting(false);
    }
  };

  const resetFlow = () => {
    setStep(STEP_CALENDAR);
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ nombre: '', apellidos: '', email: '', telefono: '', modalidad: 'Presencial', bienvenida: '' });
    setConfirmResult(null);
    setError(null);
  };

  if (loading) {
    return (
      <div className="reserva-publica" style={{ padding: '2rem', textAlign: 'center' }}>
        {t.loading}
      </div>
    );
  }

  if (notAvailable) {
    return (
      <div className="reserva-publica">
        <div className="reserva-not-available">
          <div className="reserva-not-available-icon">📅</div>
          <h2>{t.notAvailable}</h2>
          <p>{t.notAvailableDesc}</p>
        </div>
      </div>
    );
  }

  if (error && !coach) {
    return (
      <div className="reserva-publica reserva-publica-error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="reserva-publica">
      <main className="reserva-main">
        {step === STEP_CALENDAR && (
          <>
            <p className="reserva-welcome-title"><strong>{t.defaultTitle}</strong></p>
            {(() => {
              const msg = lang === 'en' ? (coach?.mensaje_bienvenida_en || coach?.mensaje_bienvenida) : coach?.mensaje_bienvenida;
              return msg ? <p className="reserva-welcome">{msg}</p> : null;
            })()}
            <h2>{t.pickDay}</h2>
            <div className="reserva-calendar">
              <div className="reserva-calendar-header">
                <button type="button" onClick={handlePrevMonth} aria-label={t.prevMonth}>
                  ‹
                </button>
                <span>
                  {new Date(month.year, month.month).toLocaleDateString(t.locale, { month: 'long', year: 'numeric' })}
                </span>
                <button type="button" onClick={handleNextMonth} aria-label={t.nextMonth}>
                  ›
                </button>
              </div>
              <div className="reserva-calendar-weekdays">
                {t.weekdays.map((d) => (
                  <span key={d}>{d}</span>
                ))}
              </div>
              <div className="reserva-calendar-days">
                {getDaysInMonth().map((day, i) => {
                  let cls = 'reserva-day';
                  if (!day) cls += ' empty';
                  else if (isDateSelectable(day)) cls += ' selectable';
                  else if (isDatePast(day)) cls += ' past';
                  else if (isDateUnavailable(day)) cls += ' unavailable';
                  else cls += ' out-of-range';
                  if (day && isDateToday(day)) cls += ' today';
                  return (
                    <button
                      key={i}
                      type="button"
                      className={cls}
                      onClick={() => selectDate(day)}
                      disabled={!day || !isDateSelectable(day)}
                    >
                      {day || ''}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {step === STEP_SLOTS && (
          <>
            <button type="button" className="reserva-back" onClick={() => setStep(STEP_CALENDAR)}>
              {t.changeDay}
            </button>
            <h2>{t.slotsFor} {selectedDate}</h2>
            {loadingSlots ? (
              <p>{t.loadingSlots}</p>
            ) : slots.length === 0 ? (
              <p>{t.noSlots}</p>
            ) : (
              <div className="reserva-slots">
                {slots.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    className="reserva-slot"
                    onClick={() => selectSlot(slot)}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {step === STEP_FORM && (
          <>
            <button type="button" className="reserva-back" onClick={() => setStep(STEP_SLOTS)}>
              {t.changeSlot}
            </button>
            <h2>{t.contactInfo}</h2>
            <p className="reserva-summary">
              {selectedDate} {t.at} {selectedSlot}
            </p>
            {error && <div className="reserva-error">{error}</div>}
            <form className="reserva-form" onSubmit={(e) => { e.preventDefault(); submitBooking(); }}>
              <label>
                {t.firstName} *
                <input name="nombre" value={form.nombre} onChange={handleFormChange} required />
              </label>
              <label>
                {t.lastName} *
                <input name="apellidos" value={form.apellidos} onChange={handleFormChange} required />
              </label>
              <label>
                {t.email} *
                <input name="email" type="email" value={form.email} onChange={handleFormChange} required />
              </label>
              <label>
                {t.phone}
                <input name="telefono" type="tel" value={form.telefono} onChange={handleFormChange} />
              </label>
              <label>
                {t.modality}
                <select name="modalidad" value={form.modalidad} onChange={handleFormChange}>
                  {t.modalities.map((m, i) => (
                    <option key={m} value={['Presencial', 'Online'][i]}>{m}</option>
                  ))}
                </select>
              </label>
              <label>
                {t.welcome}
                <textarea
                  name="bienvenida"
                  value={form.bienvenida}
                  onChange={handleFormChange}
                  placeholder={t.welcomePlaceholder}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </label>
              <button type="submit" className="reserva-submit" disabled={submitting}>
                {submitting ? t.submitting : t.confirm}
              </button>
            </form>
          </>
        )}

        {step === STEP_CONFIRM && confirmResult && (
          <div className="reserva-confirm">
            <h2>{t.confirmed}</h2>
            <p>{confirmResult.mensaje ? (lang === 'en' ? t.confirmedMsg : confirmResult.mensaje) : t.confirmedMsg}</p>
            <p className="reserva-confirm-detail">
              {selectedDate} {t.at} {selectedSlot} ({confirmResult.duracion_minutos} min)
            </p>
            <button type="button" className="reserva-submit" onClick={resetFlow}>
              {t.bookAnother}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default ReservaPublica;
